import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { mediaUrl } from "@/context/SiteContext";
import { Input } from "@/components/ui/input";
import { UploadSimple, Trash, Image as ImageIcon, CheckCircle } from "@phosphor-icons/react";

export default function MediaLibrary() {
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/admin/media").then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/admin/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Image uploaded & optimized");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); e.target.value = ""; }
  };

  const remove = async (img) => {
    if (img.used_in) {
      toast.error(`In use as the ${img.used_in} image. Replace it in Site Content first.`);
      return;
    }
    if (!window.confirm("Delete this image? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/media/${img.id}`);
      toast.success("Image deleted");
      setItems((xs) => xs.filter((x) => x.id !== img.id));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  const fmtSize = (b) => (b ? `${(b / 1024).toFixed(0)} KB` : "");

  return (
    <div data-testid="admin-media">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Media Library</h1>
          <p className="mt-1 text-sm text-slate-500">Manage all website images in one place. Images in use are labelled and protected from deletion.</p>
        </div>
        <label className="inline-flex cursor-pointer items-center gap-2 rounded-full bg-brand-purple px-5 py-2.5 text-sm font-semibold text-white hover:bg-brand-purple2">
          <UploadSimple size={18} /> {uploading ? "Uploading…" : "Upload Image"}
          <Input type="file" accept="image/png,image/jpeg,image/webp" onChange={onUpload} data-testid="media-upload" className="hidden" />
        </label>
      </div>

      {loading ? (
        <p className="mt-10 text-center text-slate-400">Loading…</p>
      ) : items.length === 0 ? (
        <div className="mt-10 rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center">
          <ImageIcon size={40} weight="duotone" className="mx-auto text-slate-300" />
          <p className="mt-3 text-slate-500">No images yet. Upload one to get started.</p>
        </div>
      ) : (
        <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((img) => (
            <div key={img.id} data-testid={`media-item-${img.id}`} className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
              <div className="aspect-video w-full overflow-hidden bg-slate-100">
                <img src={mediaUrl(img.id)} alt={img.original_filename || "media"} className="h-full w-full object-cover" loading="lazy" />
              </div>
              <div className="p-4">
                <p className="truncate text-sm font-medium text-slate-800" title={img.original_filename}>{img.original_filename || "Untitled"}</p>
                <p className="text-xs text-slate-400">{fmtSize(img.size)}</p>
                <div className="mt-3 flex items-center justify-between">
                  {img.used_in ? (
                    <span data-testid={`media-usage-${img.id}`} className="inline-flex items-center gap-1 rounded-full bg-brand-teal/10 px-2.5 py-1 text-[11px] font-semibold text-brand-teal">
                      <CheckCircle size={13} weight="fill" /> In use: {img.used_in}
                    </span>
                  ) : (
                    <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-500">Unused</span>
                  )}
                  <button
                    data-testid={`media-delete-${img.id}`}
                    onClick={() => remove(img)}
                    disabled={!!img.used_in}
                    title={img.used_in ? `In use as ${img.used_in}` : "Delete image"}
                    className={`rounded-lg p-2 ${img.used_in ? "cursor-not-allowed text-slate-300" : "text-slate-500 hover:bg-rose-50 hover:text-rose-600"}`}
                  >
                    <Trash size={18} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      <p className="mt-6 text-xs text-slate-400">Tip: assign images to the homepage hero, facilities, or about slots from <span className="font-semibold">Site Content</span>.</p>
    </div>
  );
}
